import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useSpring } from 'framer-motion';
import { Menu, X, Globe, Sun, Moon, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import '../styles/design-system.css';
import '../styles/scroll-animations.css';

const Header = () => {
  const { navigationData, contactData, language, toggleLanguage, theme, toggleTheme } = useLanguage();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [projectsDropdownOpen, setProjectsDropdownOpen] = useState(false);
  const [mobileProjectsOpen, setMobileProjectsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Mark Projetos as active when on any project subpage
  const isProjectPage = location.pathname.startsWith('/projeto-') || location.pathname === '/em-construcao';

  const projectsSubmenu = [
    { label: 'Projeto Divas', to: '/em-construcao' },
    { label: 'Projeto Águias', to: '/projeto-aguias' },
    { label: 'Projeto Prospere', to: '/projeto-prospere' },
  ];

  /* ── Scroll progress bar ─── */
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  /* ── Header scroll-linked opacity ─── */
  const rawBg = useTransform(scrollYProgress, [0, 0.05], [0, 1]);
  const bgOpacity = useSpring(rawBg, { stiffness: 120, damping: 30 });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Active section detection
      const sections = ['hero', 'about', 'services', 'tools', 'projects', 'lectures', 'contact'];
      for (const id of [...sections].reverse()) {
        const el = document.getElementById(id);
        if (el && window.scrollY >= el.offsetTop - 120) {
          setActiveSection(id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setIsMobileMenuOpen(false);
    if (window.location.pathname !== '/') {
      window.location.href = '/' + href;
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* ── Scroll Progress Bar (only visible after scrolling) ─── */}
      <motion.div
        className="scroll-progress-bar"
        style={{ scaleX, width: '100%', opacity: scaleX }}
      />

      <header className={`header-nav ${isScrolled ? 'scrolled' : ''}`} translate="no">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center"
        >
          <a
            href="#hero"
            onClick={(e) => { e.preventDefault(); handleNavClick('#hero'); }}
            style={{ color: 'var(--text-primary)', fontFamily: "'Cormorant Garamond', serif", textDecoration: 'none' }}
          >
            <span translate="no" style={{ fontSize: '1.2rem', fontWeight: 600, letterSpacing: '-0.01em' }}>
              Lucimar Bizoni
            </span>
          </a>
        </motion.div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8">
          {navigationData.links.map((link, index) => {
            const sectionId = link.href.replace('#', '');
            const isActive = isProjectPage
              ? link.label === 'Projetos'
              : activeSection === sectionId;
            const isProjects = link.label === 'Projetos';

            if (isProjects) {
              return (
                <div
                  key={link.href}
                  ref={dropdownRef}
                  className="relative"
                  onMouseEnter={() => setProjectsDropdownOpen(true)}
                  onMouseLeave={() => setProjectsDropdownOpen(false)}
                >
                  <motion.button
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    onClick={() => { handleNavClick(link.href); }}
                    className="body-small transition-colors duration-200 relative flex items-center gap-1"
                    style={{ color: isActive ? 'var(--accent-gold-400)' : 'var(--text-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    whileHover={{ y: -1 }}
                  >
                    {link.label}
                    <motion.span
                      animate={{ rotate: projectsDropdownOpen ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                      style={{ display: 'flex' }}
                    >
                      <ChevronDown size={14} />
                    </motion.span>
                    {isActive && (
                      <motion.div
                        layoutId="nav-indicator"
                        style={{ position: 'absolute', bottom: -4, left: 0, right: 0, height: 2, background: 'var(--accent-gold-400)', borderRadius: 2 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      />
                    )}
                  </motion.button>

                  {/* Dropdown */}
                  <AnimatePresence>
                    {projectsDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 8, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.96 }}
                        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          marginTop: 12,
                          background: 'var(--bg-overlay)',
                          backdropFilter: 'blur(16px)',
                          border: '1px solid var(--border-light)',
                          borderRadius: 12,
                          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                          minWidth: 200,
                          overflow: 'hidden',
                          zIndex: 100,
                        }}
                      >
                        {/* Arrow */}
                        <div style={{
                          position: 'absolute', top: -6, left: '50%', transform: 'translateX(-50%)',
                          width: 12, height: 12, background: 'var(--bg-overlay)',
                          border: '1px solid var(--border-light)',
                          borderBottom: 'none', borderRight: 'none',
                          rotate: '45deg',
                        }} />
                        <div style={{ padding: '8px 0' }}>
                          {projectsSubmenu.map((item, i) => (
                            <Link
                              key={i}
                              to={item.to}
                              onClick={() => setProjectsDropdownOpen(false)}
                              style={{
                                display: 'block',
                                padding: '10px 20px',
                                fontSize: '0.875rem',
                                color: 'var(--text-primary)',
                                textDecoration: 'none',
                                transition: 'all 0.15s',
                                fontFamily: 'Inter, sans-serif',
                              }}
                              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(184,134,11,0.08)'; e.currentTarget.style.color = 'var(--accent-gold-400)'; e.currentTarget.style.paddingLeft = '24px'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = ''; e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.paddingLeft = '20px'; }}
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            }

            return (
              <motion.a
                key={link.href}
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="body-small transition-colors duration-200 relative"
                style={{ color: isActive ? 'var(--accent-gold-400)' : 'var(--text-primary)', textDecoration: 'none' }}
                whileHover={{ y: -1 }}
              >
                {link.label}
                {isActive && (
                  <motion.div
                    layoutId="nav-indicator"
                    style={{ position: 'absolute', bottom: -4, left: 0, right: 0, height: 2, background: 'var(--accent-gold-400)', borderRadius: 2 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                )}
              </motion.a>
            );
          })}
        </nav>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-4"
        >
          <motion.a
            href={`https://wa.me/${contactData.whatsapp.replace(/\D/g, '')}?text=Olá! Gostaria de agendar.`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary hidden sm:inline-flex"
            whileHover={{ scale: 1.04, y: -2 }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
          >
            {navigationData.cta}
          </motion.a>

          <motion.button
            onClick={toggleTheme}
            className="flex items-center gap-1 p-2 rounded-full hover:bg-black/5 transition-colors"
            title={theme === 'dark' ? 'Modo Claro' : 'Modo Escuro'}
            style={{ color: 'var(--text-primary)' }}
            whileHover={{ scale: 1.1, rotate: 15 }}
            whileTap={{ scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
          >
            <AnimatePresence mode="wait">
              <motion.span
                key={theme}
                initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
                animate={{ opacity: 1, rotate: 0, scale: 1 }}
                exit={{ opacity: 0, rotate: 90, scale: 0.5 }}
                transition={{ duration: 0.2 }}
              >
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </motion.span>
            </AnimatePresence>
          </motion.button>

          <motion.button
            onClick={toggleLanguage}
            className="flex items-center gap-1 p-2 rounded-full hover:bg-black/5 transition-colors"
            title={language === 'pt' ? 'Switch to English' : 'Mudar para Português'}
            style={{ color: 'var(--text-primary)' }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Globe size={18} />
            <span className="text-sm font-medium uppercase">{language}</span>
          </motion.button>

          {/* Mobile Menu Button */}
          <motion.button
            className="btn-nav lg:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menu"
            whileTap={{ scale: 0.9 }}
          >
            <AnimatePresence mode="wait">
              <motion.span
                key={isMobileMenuOpen ? 'close' : 'open'}
                initial={{ opacity: 0, rotate: -90 }}
                animate={{ opacity: 1, rotate: 0 }}
                exit={{ opacity: 0, rotate: 90 }}
                transition={{ duration: 0.15 }}
              >
                {isMobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
              </motion.span>
            </AnimatePresence>
          </motion.button>
        </motion.div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20, clipPath: 'inset(0 0 100% 0)' }}
            animate={{ opacity: 1, y: 0, clipPath: 'inset(0 0 0% 0)' }}
            exit={{ opacity: 0, y: -10, clipPath: 'inset(0 0 100% 0)' }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-x-0 top-20 z-50 lg:hidden"
            style={{ background: 'var(--bg-overlay)', backdropFilter: 'blur(16px)' }}
          >
            <nav className="flex flex-col p-6 gap-1">
              {navigationData.links.map((link, i) => {
                const isProjects = link.label === 'Projetos';
                if (isProjects) {
                  return (
                    <div key={link.href}>
                      <motion.button
                        onClick={() => setMobileProjectsOpen(p => !p)}
                        className="body-medium py-3 border-b w-full text-left flex justify-between items-center"
                        style={{ color: 'var(--text-primary)', borderColor: 'var(--border-light)', background: 'none', border: 'none', borderBottom: `1px solid var(--border-light)`, cursor: 'pointer', padding: '12px 0' }}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.04 }}
                      >
                        {link.label}
                        <motion.span animate={{ rotate: mobileProjectsOpen ? 180 : 0 }} transition={{ duration: 0.2 }} style={{ display: 'flex' }}>
                          <ChevronDown size={16} />
                        </motion.span>
                      </motion.button>
                      <AnimatePresence>
                        {mobileProjectsOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            style={{ overflow: 'hidden' }}
                          >
                            {projectsSubmenu.map((item, j) => (
                              <Link
                                key={j}
                                to={item.to}
                                onClick={() => setIsMobileMenuOpen(false)}
                                style={{
                                  display: 'block',
                                  padding: '10px 16px',
                                  fontSize: '0.875rem',
                                  color: 'var(--accent-gold-400)',
                                  textDecoration: 'none',
                                  fontFamily: 'Inter, sans-serif',
                                  borderLeft: '2px solid var(--accent-gold-400)',
                                  marginLeft: 8,
                                  marginBottom: 4,
                                }}
                              >
                                {item.label}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }
                return (
                  <motion.a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                    className="body-medium py-3 border-b"
                    style={{ color: 'var(--text-primary)', textDecoration: 'none', borderColor: 'var(--border-light)' }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
                    whileHover={{ x: 6, color: 'var(--accent-gold-400)' }}
                  >
                    {link.label}
                  </motion.a>
                );
              })}
              <motion.a
                href={`https://wa.me/${contactData.whatsapp.replace(/\D/g, '')}?text=Olá! Gostaria de agendar.`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary mt-4 text-center block"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
              >
                {navigationData.cta}
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
